import "../i18n/i18n";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Text, Button, Tooltip } from '@fluentui/react-components';
import { BookInformation24Regular, Wifi124Regular, WifiWarning24Regular } from '@fluentui/react-icons';
import { useAppStyles } from "./styles/appStyles";

interface FooterProps {
  onNavigate?: (page: 'home' | 'subscription' | 'bdo-entities' | 'help') => void;
  hasNetworkError?: boolean;
  onRetryConnection?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate, hasNetworkError = false, onRetryConnection }) => {
  const styles = useAppStyles();
  const { t } = useTranslation();

  return (
    <footer className={styles.footer}>
      <div className={styles.footer_logo}>
        <Text as="span" size={200} weight="regular">
          © 2025-{new Date().getFullYear()} | {" "}
          <a href="https://www.adrem.energy" target="_blank" rel="noopener noreferrer">
            <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 1278 535" className={styles.footer_logo} role="img" aria-label="AdREM">
              <g transform="matrix(1,0,0,1,-81.2303,-144.372)">
                <g id="Warstwa-1">
                  <g transform="matrix(1,0,0,1,186.029,516.749)">
                    <path d="M0,38.799L61.999,38.799L30.799,-58.399L0,38.799ZM100.398,157.197L79.599,93.198L-17.2,93.198L-37.6,157.197L-104.799,157.197L-2.8,-118.398L65.599,-118.398L167.197,157.197L100.398,157.197Z" fill="rgb(36,169,119)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,489.627,483.949)">
                    <path d="M0,96.399C0,72.399 -20.399,53.2 -42.799,53.2C-65.999,53.2 -85.999,72.799 -85.999,96.399C-85.999,119.999 -65.999,139.999 -42.799,139.999C-20,139.999 0,120.399 0,96.399M2.4,-98.398L61.199,-98.398L61.199,189.998L2.4,189.998L2.4,169.198C-11.2,187.198 -36.399,194.797 -55.599,194.797C-101.999,194.797 -145.998,156.798 -145.998,96.399C-145.998,36 -101.999,-1.599 -55.599,-1.599C-36.399,-1.599 -11.2,6.001 2.4,23.6L2.4,-98.398Z" fill="rgb(36,169,119)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,651.227,619.147)">
                    <path d="M0,-165.998L0,-91.599L40,-91.599C67.599,-91.599 79.6,-109.599 79.6,-127.999C79.6,-146.397 67.199,-165.998 41.6,-165.998L0,-165.998ZM75.999,54.799L12.8,-42L0,-42L0,54.799L-62.799,54.799L-62.799,-220.797L50,-220.797C113.999,-220.797 142.798,-175.198 142.798,-129.599C142.798,-90.799 113.999,-53.6 78.399,-45.6L149.198,54.799L75.999,54.799Z" fill="rgb(36,169,119)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,889.227,453.15)">
                    <path d="M0,165.998L117.598,165.998L117.598,220.797L-62.799,220.797L-62.799,-54.799L113.998,-54.799L113.998,-0L0,-0L0,56.8L99.998,56.8L99.998,113.198L0,113.198L0,165.998Z" fill="rgb(36,169,119)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,1358.42,398.35)">
                    <path d="M0,275.596L-64.399,275.596L-82.399,118.798L-142.398,243.996L-190.397,243.996L-250.396,119.199L-268.796,275.596L-333.195,275.596L-299.195,0L-246.797,0L-166.397,172.398L-87.199,0L-33.6,0L0,275.596Z" fill="rgb(36,169,119)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,804.764,221.736)">
                    <path d="M0,-31.253L39.638,-70.891C48.268,-79.521 62.261,-79.521 70.891,-70.891L110.529,-31.253C119.159,-22.623 119.159,-8.63 110.529,0L70.891,39.638C62.261,48.268 48.268,48.268 39.638,39.638L0,0C-8.63,-8.63 -8.63,-22.623 0,-31.253" fill="rgb(36,169,119)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,631.764,221.736)">
                    <path d="M0,-31.253L39.638,-70.891C48.268,-79.521 62.261,-79.521 70.891,-70.891L110.529,-31.253C119.159,-22.623 119.159,-8.63 110.529,0L70.891,39.638C62.261,48.268 48.268,48.268 39.638,39.638L0,0C-8.63,-8.63 -8.63,-22.623 0,-31.253" fill="rgb(227,218,189)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,717.764,305.236)">
                    <path d="M0,-31.253L39.638,-70.891C48.268,-79.521 62.261,-79.521 70.891,-70.891L110.529,-31.253C119.159,-22.623 119.159,-8.63 110.529,0L70.891,39.638C62.261,48.268 48.268,48.268 39.638,39.638L0,0C-8.63,-8.63 -8.63,-22.623 0,-31.253" fill="rgb(82,141,197)" fillRule="nonzero"></path>
                  </g>
                  <g transform="matrix(1,0,0,1,544.764,305.236)">
                    <path d="M0,-31.253L39.638,-70.891C48.268,-79.521 62.261,-79.521 70.891,-70.891L110.529,-31.253C119.159,-22.623 119.159,-8.63 110.529,0L70.891,39.638C62.261,48.268 48.268,48.268 39.638,39.638L0,0C-8.63,-8.63 -8.63,-22.623 0,-31.253" fill="rgb(61,61,61)" fillRule="nonzero"></path>
                  </g>
                </g>
              </g>
            </svg>
          </a>
        </Text>
      </div>

      <div className={styles.footerRightSection}>
        {/* Network status icon */}
        <Tooltip
          withArrow
          content={
            hasNetworkError
              ? t("footer_network_error", "Brak połączenia z serwerem. Kliknij aby sprawdzić ponownie.")
              : t("footer_network_ok", "Połączenie z serwerem aktywne")
          }
          relationship="label"
        >
          <Button
            appearance="transparent"
            size="small"
            icon={
              hasNetworkError
                ? <WifiWarning24Regular style={{ color: '#d13438' }} />
                : <Wifi124Regular style={{ color: '#23A977' }} />
            }
            onClick={hasNetworkError ? onRetryConnection : undefined}
            style={{
              cursor: hasNetworkError ? 'pointer' : 'default',
              opacity: hasNetworkError ? 1 : 0.6
            }}
          />
        </Tooltip>

        <Tooltip
          withArrow
          content={t("footer_tooltip_info", "Informacje o dodatku")}
          relationship="label"
        >
          <Button
            appearance="transparent"
            size="small"
            icon={<BookInformation24Regular style={{ color: '#23A977' }}/>}
            onClick={() => onNavigate?.('help')}
          />
        </Tooltip>
      </div>
    </footer>
  );
};

export default Footer;